import { Component, OnDestroy, OnInit, Renderer2, AfterViewInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { getAuth, getRedirectResult } from 'firebase/auth';
import { combineLatest, filter, firstValueFrom, ReplaySubject, takeUntil } from 'rxjs';

import { LaunchDarklyService } from 'app/core/services/launch-darkly/launch-darkly.service';
import {
  EarlyAccessMessage,
  LaunchDarklyFlags,
  WarningMessage,
} from 'app/core/services/launch-darkly/launch-darkly.types';
import { AuthenticationState, coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import { AuthService } from 'app/services/auth.service';
import { GuestAuthService, isDataPassUser } from 'app/services/guest-auth.service';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import SocialRoomSet from 'entity/SocialRoomSet';
import SocialPermissionType from 'enums/SocialPermissionType';

import { DeepLinkNavService } from '../../deep-link/deepLinkNav.service';
import { ModalDialogComponent, ModalDialogData, ModalDialogResult } from '../modal-dialog/modal-dialog.component';
import { BrowserService } from '../services/browser.service';
import { DeviceService } from '../services/device/device.service';
import { SceneService } from '../services/scene.service';
import { VideoChatService } from '../services/videochat.service';
import { actions, socialAppSelectors } from '../state/app';
import { GUEST_SIGN_IN_ERROR } from '../strings';

@Component({
  selector: 'app-guest-page',
  templateUrl: './guest-page.component.html',
  styleUrls: ['./guest-page.component.scss'],
  standalone: false,
})
export class GuestPageComponent extends Loggable implements OnInit, OnDestroy, AfterViewInit {
  public showGuestCard = true;
  public showSignUpCard?: boolean;
  public subTitle?: string;
  public lockedGuest?: boolean;
  public showLoader = true;
  public dataPassFirstName?: string;
  public dataPassLastName?: string;
  public dataPassEmail?: string;
  public dataPass = false;
  public social?: Social;
  protected scootaverse: Social | undefined;
  public connectedRoomSet?: Social[];
  public warningMessage?: WarningMessage;
  public earlyAccessMessage?: EarlyAccessMessage;

  private formBuilder: UntypedFormBuilder = new UntypedFormBuilder();
  protected guestSignInForm: UntypedFormGroup;
  protected ownersName?: string;
  protected roomsName?: string;
  protected showGuestFormMobile? = false;
  protected displayWarning = false;
  protected displayEarlyAccess = false;
  protected showRegister = false;
  protected mobileRegister = false;
  protected pageLoaded = false;
  protected firstNameErrorMessage = 'First name must not exceed 225 characters.';
  protected lastNameErrorMessage = 'Last name must not exceed 225 characters.';

  private destroyed$ = new ReplaySubject<boolean>(1);
  protected selectedIndex = 0;
  private stateURL: string | undefined;
  private testUser = false;

  public constructor(
    private router: Router,
    private deepNavService: DeepLinkNavService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private videoChatService: VideoChatService,
    private guestAuthService: GuestAuthService,
    private sceneService: SceneService,
    private dialog: MatDialog,
    private deviceService: DeviceService,
    private readonly renderer2: Renderer2,
    private browserService: BrowserService,
    private ldService: LaunchDarklyService,
    private store: Store,
  ) {
    super();
    this.store
      .select(coreSelectors.getAuthenticationState)
      .pipe(filter((state) => state === AuthenticationState.AUTHENTICATED))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: async () => {
          const stateUrl = this.route.snapshot.queryParamMap.get('return');
          await this.deepNavService.navToDeepLink(stateUrl ?? '', this.router);
        },
      });

    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ isTestUser }) => (this.testUser = isTestUser));

    // TODO: this data is not currently in state. find out why.
    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomSet)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((connectedRoomSet) => {
        this.scootaverse = connectedRoomSet;
        this.connectedRoomSet = connectedRoomSet.socials || [];
      });

    this.guestSignInForm = this.formBuilder.group({
      firstName: new UntypedFormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(225)]),
      lastName: new UntypedFormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(225)]),
    });
  }

  public async ngOnInit(): Promise<void> {
    // When initially joining social
    // Update LD user context
    this.pageLoaded = false;
    this.stateURL = this.route.snapshot.queryParamMap.get('return') ?? '';

    try {
      await this.ldService.updateUserContext();
    } catch (e) {
      this.error('Failed to update LD user context with error: ', e);
    }

    this.showGuestCard = true;
    this.getDataPassParams();
    this.canJoinSocial()
      .catch((e) => {
        this.error('Failed to join social with error: ', e);
        this.showLoader = false;
      })
      .finally(() => {
        this.showLoader = false;
      });

    this.renderer2.addClass(document.body.parentElement, 'socialBodyClass');
    this.renderer2.addClass(document.body, 'socialBodyClass');

    // Set Flag for new Login Page
    combineLatest([
      getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_error_banner),
      getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_early_access_banner),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([errorBanner, earlyAccessMessage]) => {
        this.warningMessage = errorBanner;
        this.earlyAccessMessage = earlyAccessMessage;
        setTimeout(() => {
          this.pageLoaded = true;
        }, 300);
      });
  }

  // TODO: this is a copy of the method in new-sign-up-card.component.ts, DRY it up
  public async ngAfterViewInit(): Promise<void> {
    const check = await this.browserService.checkBrowserAvailability().catch((error) => {
      this.error('Failed to check browser availability with error: ', error);
    });
    if (!check) {
      this.error('No check found');
      return;
    }

    this.displayWarning = check.isAvailable && check.showWarning;
    this.displayEarlyAccess = check.isAvailable && check.earlyAccess;

    // Check if browser or device are available
    try {
      await this.deviceService.checkBrowserAndDevice(check);
    } catch (error) {
      this.error('Failed to Check Browser and Device with error: ', error);
    }
    // checking if returning from oAuth
    const auth = getAuth();
    if (auth) {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          this.info('getRedirectResult', result);
          await this.authService.startSignInProcess({ reload: false, returnURL: this.stateURL });
          this.showLoader = false;
        } else {
          this.showLoader = false;
          this.info('no oAuth redirect result found');
        }
      } catch (error) {
        this.showLoader = false;
        this.error('getRedirectResult error', { error });
      }
    } else {
      this.error('auth is undefined');
      this.showLoader = false;
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    void this.sceneService.destroy();
    this.renderer2.removeClass(document.body.parentElement, 'socialBodyClass');
    this.renderer2.removeClass(document.body, 'socialBodyClass');
  }

  public get isDataPassUser(): boolean {
    return isDataPassUser();
  }

  public async canJoinSocial(): Promise<void> {
    try {
      this.showLoader = true;
      this.info(`Joining Social as ${this.testUser ? 'Test User' : 'Normal User'}`);

      // See social-guard-service navigate to `web/social/guest` for where these queryParams are set
      let socialId = this.route.snapshot.queryParamMap.get('social');
      const setId = this.route.snapshot.queryParamMap.get('roomSet');
      const region = await this.videoChatService.getRegion().catch((error) => {
        this.error('Failed to get region with error: ', error);
      });
      if (!region) {
        this.error('No region found');
        this.showLoader = false;
        return;
      }
      if (!socialId && !!setId) {
        const socialRoomSet: SocialRoomSet = await firstValueFrom(this.videoChatService.getSocialRoomSet(setId));
        const socialsFromSet = socialRoomSet?.socials;

        if (socialsFromSet && socialsFromSet[0].socialId) {
          socialId = socialsFromSet[0].socialId;
        }
      }

      if (!socialId) {
        this.error('No socialId found in query params');
        this.showLoader = false;
        return;
      }

      try {
        this.social = await firstValueFrom(this.videoChatService.getSocial(socialId, false, region));
      } catch (e) {
        this.error('Failed to get social with error: ', e);
      }
      if (!this.social) {
        this.error('No social found');
        this.showLoader = false;
        return;
      }
      this.store.dispatch(actions.LOAD_SOCIAL({ social: this.social }));
      this.showLoader = false;

      this.ownersName = formatUserName(this.social.user ?? {});
      this.roomsName = this.social.name;

      if (isDataPassUser()) {
        this.dataPass = true;
        this.guestSignIn();
      }

      if (this.social.socialPermissionType !== SocialPermissionType.PUBLIC) {
        this.lockGuest();
        return;
      }

      this.subTitle = 'To join, type your display name and click Continue as Guest.';
      this.lockedGuest = false;
    } catch (error) {
      this.error('Failed to join social with error: ', error);
      this.lockGuest();
      this.showLoader = false;
      if ((error as { status: number }).status === 404) {
        this.indicateRoomEnd();
      }
    }
  }

  public getDataPassParams(): void {
    this.dataPassFirstName = this.route.snapshot.queryParamMap.get('firstname') ?? '';
    this.dataPassLastName = this.route.snapshot.queryParamMap.get('lastname') ?? '';
    this.dataPassEmail = this.route.snapshot.queryParamMap.get('email') ?? '';
  }

  /**
   * Indicate to the user that the room has ended and  navigate to the home page.
   */
  private indicateRoomEnd(): void {
    const data: ModalDialogData = {
      title: 'Room Closed',
      text: 'Sorry but this room has already been closed. Please create a new room to join.',
      primaryButtonText: 'Okay',
    };

    this.dialog
      .open<ModalDialogComponent, ModalDialogData, ModalDialogResult>(ModalDialogComponent, { data, width: '540px' })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((_result) => {
        this.showLoader = false;
        this.router.navigate(['/']).catch((error) => {
          this.error(error);
        });
      });
  }

  public lockGuest(): void {
    this.subTitle =
      'The host of this event has made this event accessible only to members of the company, to continue, sign in, or sign up using your company email address.';
    this.lockedGuest = true;
  }

  public signUp(): void {
    this.showGuestCard = false;
    this.showSignUpCard = true;
  }

  public showGuestMobile(): void {
    this.showGuestFormMobile = true;
  }

  public confirmAccount(returnURL: string): void {
    this.router.navigate(['welcome/confirmAccount'], { queryParams: { return: returnURL } }).catch((error) => {
      this.error(error);
    });
  }

  public forgotPassword(returnURL: string): void {
    this.router.navigate(['welcome/forgotPassword'], { queryParams: { return: returnURL } }).catch((error) => {
      this.error(error);
    });
  }

  public guestSignIn(): void {
    this.showLoader = true;
    this.info('Guest Sign In');

    const user = this.dataPass
      ? {
          firstName: this.dataPassFirstName,
          lastName: this.dataPassLastName,
          emailAddress: this.dataPassEmail,
        }
      : {
          firstName: this.guestSignInForm.get('firstName')?.value.trim(),
          lastName: this.guestSignInForm.get('lastName')?.value.trim(),
        };

    this.guestAuthService
      .guestSignIn(user)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.showLoader = false;
        },
        error: () => {
          this.handleGuestError();
        },
      });
  }

  public handleGuestError(): void {
    //If in this state with datapass, then do not use it again, but require user to sign in with guest manually.
    this.showLoader = false;
    this.dataPass = false;
    this.openErrorDialog();
    this.authService.signOutUser();
  }

  public openErrorDialog(): void {
    const dialogRef = this.dialog.open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, {
      data: GUEST_SIGN_IN_ERROR,
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe();
  }

  public handleRegisterButton(): void {
    this.showGuestCard = false;
    this.showSignUpCard = true;
    this.showRegister = true;
    this.selectedIndex = 1;
  }

  // Handle behavior if register button was clicked on login mobile card
  public showMobileRegister(mobileRegister: boolean): void {
    this.mobileRegister = mobileRegister;
  }

  public checkFirstNameExceedsLength(): boolean {
    return this.guestSignInForm.get('firstName')?.value.length > 225;
  }

  public checkLastNameExceedsLength(): boolean {
    return this.guestSignInForm.get('lastName')?.value.length > 225;
  }

  public checkIfFormIsValid(): boolean {
    return this.guestSignInForm.valid;
  }
}
